import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Eye, EyeSlash, ShieldCheck } from "@phosphor-icons/react";

const COMPANY_LOGO = process.env.REACT_APP_COMPANY_LOGO;
const COMPANY_NAME = process.env.REACT_APP_COMPANY_NAME || "Vision AcryGlas";

export default function AdminLoginPage() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      toast.error("Please fill in all fields");
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/admin/login`, { email, password });
      localStorage.setItem("adminToken", response.data.token);
      localStorage.setItem("admin", JSON.stringify(response.data.admin));
      toast.success("Welcome, Admin!");
      navigate("/admin");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Invalid admin credentials");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-[#0A0A0A]" data-testid="admin-login-page">
      {/* Logo Header */}
      <div className="py-8 px-4 flex flex-col items-center">
        <img 
          src={COMPANY_LOGO} 
          alt={COMPANY_NAME} 
          className="h-16 object-contain mb-2"
          data-testid="company-logo"
        />
        <div className="flex items-center gap-2 mt-2">
          <ShieldCheck size={20} className="text-[#0055FF]" weight="fill" />
          <p className="text-sm text-[#9CA3AF] font-medium">Admin Portal</p>
        </div>
      </div>
      
      {/* Login Form */}
      <div className="flex-1 bg-white rounded-t-3xl px-6 pt-8 pb-12">
        <div className="max-w-md mx-auto">
          <h1 className="text-3xl font-bold text-[#0A0A0A] mb-2" style={{ fontFamily: "'Chivo', sans-serif" }}>
            Admin Login
          </h1>
          <p className="text-[#4B5563] mb-8">Access the management dashboard</p>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <Label htmlFor="email" className="text-[#0A0A0A] font-medium">
                Admin Email
              </Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="admin@visionacryglas.com"
                className="mt-1.5 h-12 border-[#E5E7EB] focus:border-[#0055FF] focus:ring-[#0055FF]/10"
                data-testid="admin-email-input"
              />
            </div>

            <div>
              <Label htmlFor="password" className="text-[#0A0A0A] font-medium">
                Password
              </Label>
              <div className="relative mt-1.5">
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter admin password"
                  className="h-12 pr-12 border-[#E5E7EB] focus:border-[#0055FF] focus:ring-[#0055FF]/10"
                  data-testid="admin-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-[#9CA3AF] hover:text-[#4B5563]"
                >
                  {showPassword ? <EyeSlash size={20} /> : <Eye size={20} />}
                </button>
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full h-12 bg-[#0A0A0A] hover:bg-[#1a1a1a] text-white font-semibold rounded-full mt-6"
              data-testid="admin-login-button"
            >
              {loading ? "Signing in..." : "Sign In as Admin"}
            </Button>
          </form>

          <p className="text-center mt-8 text-[#9CA3AF] text-sm">
            Not an admin?{" "}
            <button 
              onClick={() => navigate("/login")}
              className="text-[#0055FF] font-medium hover:underline"
            >
              Go to Sales Login
            </button>
          </p>
        </div>
      </div>
    </div>
  );
}
